/**
 * Restaurant Favorites Service
 * Manages customer favorite restaurants using EXISTING restaurant_favorites table
 * Schema: references restaurants table with restaurant_id FK
 */

import { supabase } from './supabase';
import { logger } from './logger';

export const restaurantFavoritesService = {
  /**
   * Get all favorite restaurants for a user (with restaurant details)
   * @param {string} userId - User UUID
   * @returns {Promise<Array>} Array of favorite restaurants with full restaurant data
   */
  async getFavoriteRestaurants(userId) {
    try {
      logger.debug('Getting favorite restaurants', { userId });

      const { data, error } = await supabase
        .from('restaurant_favorites')
        .select(`
          *,
          restaurants (*)
        `)
        .eq('user_id', userId)
        .eq('preference_type', 'favorite')
        .order('created_at', { ascending: false });

      if (error) throw error;

      logger.debug('Found favorite restaurants', { count: data?.length || 0 });
      return data || [];
    } catch (error) {
      logger.error('Error getting favorite restaurants', { error: error.message, userId });
      throw error;
    }
  },

  /**
   * Add a restaurant to favorites
   * @param {string} userId - User UUID
   * @param {string} restaurantId - Restaurant UUID from restaurants table
   * @param {string} notes - Optional notes about this restaurant
   * @returns {Promise<Object>} Created favorite entry
   */
  async addFavoriteRestaurant(userId, restaurantId, notes = null) {
    try {
      logger.debug('Adding restaurant to favorites', { userId, restaurantId });

      const insertData = {
        user_id: userId,
        restaurant_id: restaurantId,
        preference_type: 'favorite',
        notes: notes
      };

      const { data, error } = await supabase
        .from('restaurant_favorites')
        .insert(insertData)
        .select(`
          *,
          restaurants (*)
        `)
        .single();

      if (error) {
        // Check if already exists
        if (error.code === '23505') { // Unique constraint violation
          throw new Error('This restaurant is already in your favorites.');
        }
        throw error;
      }

      logger.debug('Restaurant added to favorites successfully', { favoriteId: data.id });
      return data;
    } catch (error) {
      logger.error('Error adding restaurant to favorites', {
        error: error.message,
        userId
      });
      throw error;
    }
  },

  /**
   * Remove a restaurant from favorites
   * @param {string} userId - User UUID
   * @param {string} restaurantId - Restaurant UUID from restaurants table
   * @returns {Promise<boolean>} Success status
   */
  async removeFavoriteRestaurant(userId, restaurantId) {
    try {
      logger.debug('Removing restaurant from favorites', { userId, restaurantId });

      const { error } = await supabase
        .from('restaurant_favorites')
        .delete()
        .eq('user_id', userId)
        .eq('restaurant_id', restaurantId);

      if (error) throw error;

      logger.debug('Restaurant removed from favorites successfully');
      return true;
    } catch (error) {
      logger.error('Error removing restaurant from favorites', {
        error: error.message,
        userId,
        restaurantId
      });
      throw error;
    }
  },

  /**
   * Check if a restaurant is favorited by user
   * @param {string} userId - User UUID
   * @param {string} restaurantId - Restaurant UUID from restaurants table
   * @returns {Promise<boolean>} True if favorited
   */
  async isFavorite(userId, restaurantId) {
    try {
      logger.debug('Checking if restaurant is favorite', { userId, restaurantId });

      const { data, error } = await supabase
        .from('restaurant_favorites')
        .select('id')
        .eq('user_id', userId)
        .eq('restaurant_id', restaurantId)
        .eq('preference_type', 'favorite')
        .limit(1);

      if (error) throw error;

      const isFav = data && data.length > 0;
      logger.debug('Restaurant favorite status', { restaurantId, isFavorite: isFav });
      return isFav;
    } catch (error) {
      logger.error('Error checking favorite status', {
        error: error.message,
        userId,
        restaurantId
      });
      return false;
    }
  },

  /**
   * Increment times_booked counter when a meeting is booked
   * @param {string} userId - User UUID
   * @param {string} restaurantId - Restaurant UUID
   * @returns {Promise<Object>} Updated favorite or null
   */
  async incrementTimesBooked(userId, restaurantId) {
    try {
      logger.debug('Incrementing times booked', { userId, restaurantId });

      // Get current favorite
      const { data: current } = await supabase
        .from('restaurant_favorites')
        .select('times_booked')
        .eq('user_id', userId)
        .eq('restaurant_id', restaurantId)
        .single();

      const newCount = (current?.times_booked || 0) + 1;

      const { data, error } = await supabase
        .from('restaurant_favorites')
        .update({
          times_booked: newCount,
          last_booked_date: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('restaurant_id', restaurantId)
        .select()
        .single();

      if (error) throw error;

      logger.debug('Times booked incremented', { restaurantId, newCount });
      return data;
    } catch (error) {
      logger.error('Error incrementing times booked', {
        error: error.message,
        userId,
        restaurantId
      });
      // Non-critical, don't throw
      return null;
    }
  },

  /**
   * Toggle favorite status (add if not exists, remove if exists)
   * @param {string} userId - User UUID
   * @param {string} restaurantId - Restaurant UUID
   * @param {string} notes - Optional notes
   * @returns {Promise<Object>} Result with action taken
   */
  async toggleFavorite(userId, restaurantId, notes = null) {
    try {
      const isFav = await this.isFavorite(userId, restaurantId);

      if (isFav) {
        await this.removeFavoriteRestaurant(userId, restaurantId);
        return { action: 'removed', isFavorite: false };
      } else {
        const data = await this.addFavoriteRestaurant(userId, restaurantId, notes);
        return { action: 'added', isFavorite: true, data };
      }
    } catch (error) {
      logger.error('Error toggling favorite', {
        error: error.message,
        userId
      });
      throw error;
    }
  }
};

export default restaurantFavoritesService;
